import React, { createContext, useContext } from "react";
import classNames from "classnames";
import { observer } from "mobx-react";
import { useRoutes } from "react-router-dom";


import { AppLogo } from "modules/root/components/logo/AppLogo";
import { MainMenuWithRouter } from "modules/root/components/menu/MainMenu";
import { OrderListWrapper } from "modules/orders-manage/list/OrderListWrapper";
import { buildRoutes } from "modules/root/routes";
import { LocationBroadcaster } from "modules/common/components/routing/withRouter";
import { useMst } from "modules/root/models/hooks";
import { AppAnnounce } from "../version/AppAnnounce";
import { AppVersion } from "../version/AppVersion";
import versionStyles from "../version/AppVersion.module.scss";
import { Notifications } from "./Notifications";
import { User } from "./User";
import styles from "./App.module.scss";

/**
 * Каркас приложения: шапка, меню, список проектов и область страницы.
 *
 * Живёт отдельным компонентом, потому что в react-router 6 всё, что пользуется
 * роутером, обязано быть внутри `<RouterProvider>` — а меню, пользователь и
 * список проектов роутером пользуются. Сам провайдер поднят в `App`, туда же
 * вынесено состояние раскладки: сюда оно приходит через `ShellContext`.
 */
export const AppShell = observer(() => {
    const store = useMst();
    const { hideMenu, setHideMenu, isMobile, overlayVisible, orderListVisible, closeOverlays, onLogout } = useShell();

    const { loggedIn } = store.session;

    const style: React.CSSProperties = { width: !hideMenu ? "308px" : "0px" };
    const logoStyle: React.CSSProperties = { width: !hideMenu ? "250px" : "0px" };
    const navStyle: React.CSSProperties = { width: !hideMenu ? "308px" : "0px" };

    return (
        <>
            <LocationBroadcaster />
            {loggedIn && <User store={store.session} onLogout={onLogout} employerStore={store.employee.details} />}
            {loggedIn && (
                <div className={styles.nav_container} style={navStyle}>
                    {!orderListVisible && (
                        <button
                            type="button"
                            className={classNames(styles.burger, { [styles.open]: !hideMenu })}
                            aria-label="Меню"
                            aria-expanded={!hideMenu}
                            onClick={() => setHideMenu(!hideMenu)}
                        >
                            <span className={`${styles.line} ${styles.line1}`} />
                            <span className={`${styles.line} ${styles.line2}`} />
                            <span className={`${styles.line} ${styles.line3}`} />
                        </button>
                    )}
                    <div className={styles.mainLogo} style={logoStyle}>
                        <AppLogo />
                        <div className={versionStyles.versionColumn}>
                            <AppVersion version={store.apiVersion} changelog={store.changelog} prefix="1." />
                            <AppAnnounce announces={store.announces} />
                        </div>

                        <i className={`fa fa-pencil-square-o ${styles.preload}`} aria-hidden="true" />
                    </div>
                </div>
            )}
            {loggedIn && (
                <div className={styles.leftColumn} style={style}>
                    <MainMenuWithRouter
                        store={store}
                        hide={hideMenu}
                        setHideMenu={() => setHideMenu(!hideMenu)}
                        isMobile={isMobile}
                    />
                    <OrderListWrapper store={store.orders.list} access={store.session.access} isMobile={isMobile} />
                </div>
            )}
            {loggedIn && (
                <div
                    className={classNames(styles.backdrop, { [styles.visible]: overlayVisible })}
                    onClick={closeOverlays}
                />
            )}
            <div className={styles.mainContainer}>
                <Notifications />
                <AppRoutes />
            </div>
        </>
    );
});

/**
 * Дерево маршрутов собирается на каждом рендере: половина из них зависит от прав
 * пользователя, а те меняются в рантайме (вход, выход, обновление доступа по SignalR).
 * Поэтому маршруты живут в `useRoutes`, а не в самом роутере — тот создаётся один раз.
 */
const AppRoutes = observer(() => {
    const store = useMst();

    return useRoutes(buildRoutes(store));
});

interface ShellValue {
    hideMenu: boolean;
    setHideMenu: (value: boolean) => void;
    isMobile: boolean;
    overlayVisible: boolean;
    orderListVisible: boolean;
    closeOverlays: () => void;
    onLogout: () => void;
}

const ShellContext = createContext<ShellValue | null>(null);

export const ShellProvider = ShellContext.Provider;

const useShell = () => {
    const value = useContext(ShellContext);

    if (!value) {
        throw new Error("AppShell отрисован вне ShellProvider");
    }

    return value;
};
